import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

// Define translations
const connectTranslations = {
  en: {
    title: "Let's Connect",
    subtitle: "I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision. Let's create something remarkable together.",
    nameLabel: "Your Name",
    emailLabel: "Your Email",
    subjectLabel: "Subject",
    messageLabel: "Your Message",
    sendButton: "Send Message",
    getTouchTitle: "Get in Touch",
    getTouchText: "Feel free to reach out for collaborations, speaking engagements, or just to say hello!",
    email: "Email:",
    socialTitle: "Connect on Social Media",
    socialText: "Follow for updates, insights, and more:",
    businessTitle: "Business Inquiries",
    businessText: "For business opportunities and partnerships:",
    thankYouAlert: "Thank you for your message! I will get back to you soon."
  },
  tr: {
    title: "İletişime Geçelim",
    subtitle: "Yeni projeler, yaratıcı fikirler veya vizyonunuzun bir parçası olma fırsatları hakkında konuşmaya her zaman açığım. Birlikte olağanüstü bir şey yaratalım.",
    nameLabel: "Adınız",
    emailLabel: "E-posta Adresiniz",
    subjectLabel: "Konu",
    messageLabel: "Mesajınız",
    sendButton: "Mesaj Gönder",
    getTouchTitle: "İletişime Geçin",
    getTouchText: "İşbirlikleri, konuşma etkinlikleri veya sadece merhaba demek için iletişime geçmekten çekinmeyin!",
    email: "E-posta:",
    socialTitle: "Sosyal Medyada Bağlantı Kurun",
    socialText: "Güncellemeler, içgörüler ve daha fazlası için takip edin:",
    businessTitle: "İş Talepleri",
    businessText: "İş fırsatları ve ortaklıklar için:",
    thankYouAlert: "Mesajınız için teşekkürler! En kısa sürede size geri döneceğim."
  },
  fr: {
    title: "Connectons-nous",
    subtitle: "Je suis toujours ouvert à discuter de nouveaux projets, d'idées créatives ou d'opportunités de faire partie de votre vision. Créons ensemble quelque chose de remarquable.",
    nameLabel: "Votre Nom",
    emailLabel: "Votre Email",
    subjectLabel: "Sujet",
    messageLabel: "Votre Message",
    sendButton: "Envoyer le Message",
    getTouchTitle: "Entrer en Contact",
    getTouchText: "N'hésitez pas à me contacter pour des collaborations, des conférences ou simplement pour dire bonjour!",
    email: "Email:",
    socialTitle: "Connectez-vous sur les Réseaux Sociaux",
    socialText: "Suivez pour les mises à jour, les idées et plus encore:",
    businessTitle: "Demandes Professionnelles",
    businessText: "Pour les opportunités commerciales et partenariats:",
    thankYouAlert: "Merci pour votre message! Je vous répondrai bientôt."
  },
  es: {
    title: "Conectémonos",
    subtitle: "Siempre estoy abierto a discutir nuevos proyectos, ideas creativas u oportunidades para ser parte de tu visión. Creemos algo notable juntos.",
    nameLabel: "Su Nombre",
    emailLabel: "Su Correo Electrónico",
    subjectLabel: "Asunto",
    messageLabel: "Su Mensaje",
    sendButton: "Enviar Mensaje",
    getTouchTitle: "Ponerse en Contacto",
    getTouchText: "¡No dude en comunicarse para colaboraciones, conferencias o simplemente para saludar!",
    email: "Correo electrónico:",
    socialTitle: "Conectar en Redes Sociales",
    socialText: "Siga para actualizaciones, ideas y más:",
    businessTitle: "Consultas de Negocios",
    businessText: "Para oportunidades comerciales y asociaciones:",
    thankYouAlert: "¡Gracias por su mensaje! Me pondré en contacto con usted pronto."
  },
  ar: {
    title: "لنتواصل",
    subtitle: "أنا دائمًا منفتح لمناقشة مشاريع جديدة، أفكار إبداعية، أو فرص لأكون جزءًا من رؤيتك. دعنا نبتكر شيئًا مميزًا معًا.",
    nameLabel: "اسمك",
    emailLabel: "بريدك الإلكتروني",
    subjectLabel: "الموضوع",
    messageLabel: "رسالتك",
    sendButton: "إرسال الرسالة",
    getTouchTitle: "ابقَ على تواصل",
    getTouchText: "لا تتردد في التواصل للتعاون، أو المشاركة في الفعاليات، أو حتى لمجرد إلقاء التحية!",
    email: "البريد الإلكتروني:",
    socialTitle: "تواصل عبر وسائل التواصل الاجتماعي",
    socialText: "تابعنا للحصول على التحديثات والرؤى والمزيد:",
    businessTitle: "استفسارات الأعمال",
    businessText: "لفرص العمل والشراكات:",
    thankYouAlert: "شكرًا لك على رسالتك! سأعود إليك قريبًا."
  }
};

const ConnectSection = styled.section`
  padding: 120px 0 80px;
  background-color: white;
  color: black;
`;

const ConnectContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ConnectHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const ConnectTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  color: black;
  
  &:after {
    display: none;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ConnectSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  color: #333;
`;

const ConnectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled(motion.form)`
  background-color: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid #eaeaea;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: black;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e1e1e1;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const FormButton = styled(motion.button)`
  background-color: #000;
  color: white;
  border: none;
  padding: 15px 30px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #333;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const InfoItem = styled(motion.div)`
  margin-bottom: 30px;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  position: relative;
  display: inline-block;
  color: black;
  
  &:after {
    display: none;
  }
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 10px;
  color: #333;
`;

const InfoLink = styled.a`
  color: #000;
  font-weight: 600;
  transition: color 0.3s ease;
  
  &:hover {
    color: #555;
    text-decoration: underline;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #000;
  color: #fff;
  font-size: 1.2rem;
  transition: background-color 0.3s ease;
  overflow: hidden;
  
  &:hover {
    background-color: #333;
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
`;

const Connect: React.FC = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // SEO metadata for Connect page
  const connectSeoData = {
    en: {
      title: 'Connect - Barkin Sayin | Contact & Collaboration',
      description: 'Get in touch with Barkin Sayin for business inquiries, speaking engagements, or collaboration opportunities in fintech, smart TV technology, and digital transformation.',
      keywords: [
        'contact Barkin Sayin',
        'business collaboration',
        'fintech partnership',
        'speaking engagement',
        'investment opportunities',
        'technology consultation',
        'Trade on TV contact'
      ]
    },
    tr: {
      title: 'İletişim - Barkın Sayın | İletişim ve İşbirliği',
      description: 'Fintech, akıllı TV teknolojisi ve dijital dönüşüm alanlarında iş sorguları, konuşma davetleri veya işbirliği fırsatları için Barkın Sayın ile iletişime geçin.',
      keywords: [
        'Barkın Sayın iletişim',
        'iş işbirliği',
        'fintech ortaklık',
        'konuşma davetleri',
        'yatırım fırsatları',
        'teknoloji danışmanlığı',
        'Trade on TV iletişim'
      ]
    }
  };
  
  const seoTitle = connectSeoData[language as keyof typeof connectSeoData]?.title || connectSeoData.en.title;
  const seoDescription = connectSeoData[language as keyof typeof connectSeoData]?.description || connectSeoData.en.description;
  const seoKeywords = connectSeoData[language as keyof typeof connectSeoData]?.keywords || connectSeoData.en.keywords;

  // Get text based on the current language
  const getText = (key: keyof typeof connectTranslations.en): string => {
    if (language === 'en' || language === 'tr' || language === 'fr' || language === 'es' || language === 'ar') {
      return connectTranslations[language as keyof typeof connectTranslations][key];
    }
    return connectTranslations.en[key];
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would normally send the form data to your backend
    alert(getText('thankYouAlert'));
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      
      <ConnectSection>
        <ConnectContainer>
          <ConnectHeader>
            <ConnectTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {getText('title')}
            </ConnectTitle>
            
            <ConnectSubtitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {getText('subtitle')}
            </ConnectSubtitle>
          </ConnectHeader>
          
          <ConnectGrid>
            <ContactForm
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              onSubmit={handleSubmit}
            >
              <FormGroup>
                <FormLabel htmlFor="name">{getText('nameLabel')}</FormLabel>
                <FormInput 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">{getText('emailLabel')}</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="subject">{getText('subjectLabel')}</FormLabel>
                <FormInput 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  value={formData.subject}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">{getText('messageLabel')}</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormButton
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {getText('sendButton')}
              </FormButton>
            </ContactForm>
            
            <ContactInfo>
              <InfoItem
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <InfoTitle>{getText('getTouchTitle')}</InfoTitle>
                <InfoText>{getText('getTouchText')}</InfoText>
                <InfoText>{getText('email')} <InfoLink href="mailto:contact@barkinsayin.com">contact@barkinsayin.com</InfoLink></InfoText>
              </InfoItem>
              
              <InfoItem
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <InfoTitle>{getText('socialTitle')}</InfoTitle>
                <InfoText>{getText('socialText')}</InfoText>
                <SocialLinks>
                  <SocialIcon 
                    href="https://www.instagram.com/barkinsayin_official/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </SocialIcon>
                  <SocialIcon 
                    href="https://www.linkedin.com/in/barkinsayin/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </SocialIcon>
                  <SocialIcon 
                    href="https://www.youtube.com/@BarkinSayin" 
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                    </svg>
                  </SocialIcon>
                </SocialLinks>
              </InfoItem>
              
              <InfoItem
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <InfoTitle>{getText('businessTitle')}</InfoTitle>
                <InfoText>{getText('businessText')}</InfoText>
                <InfoText>{getText('email')} <InfoLink href="mailto:business@barkinsayin.com">business@barkinsayin.com</InfoLink></InfoText>
              </InfoItem>
            </ContactInfo>
          </ConnectGrid>
        </ConnectContainer>
      </ConnectSection>
    </motion.div>
  );
};

export default Connect; 